import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Breadcrumbs,
  Link,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  DragIndicator as DragIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import exerciseService from '../../services/exercise.service';
import exerciseContainerService from '../../services/exerciseContainerService';
import authService from '../../services/auth.service';
import { Exercise, ExercisePattern, ExerciseContainer } from '../../types';
import { toast } from 'react-toastify';
import PdfViewer from '../../components/PdfViewer';
import ParentDropZone from '../../components/ParentDropZone';
import PatternSelectionDialog from '../../components/PatternSelectionDialog';

// Sortable Item Component for both exercises and containers
interface SortableItemProps {
  item: Exercise | ExerciseContainer;
  isContainer?: boolean;
  isSelected: boolean;
  onSelect: (item: Exercise | ExerciseContainer, isContainer: boolean) => void;
  onDeleteContainer?: (container: ExerciseContainer) => void;
  onRenameItem?: (item: Exercise | ExerciseContainer, isContainer: boolean) => void;
  onDeleteExercise?: (exercise: Exercise) => void;
  onTogglePublic?: (exercise: Exercise, isPublic: boolean) => void;
  activeItemType?: string;
}

const SortableItem: React.FC<SortableItemProps> = ({
  item,
  isContainer = false,
  isSelected,
  onSelect,
  onDeleteContainer,
  onRenameItem,
  onDeleteExercise,
  onTogglePublic,
  activeItemType,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: `${isContainer ? 'container' : 'exercise'}-${item.id}` });

  const style = {
    transform: (isContainer && activeItemType === 'exercise') ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const user = authService.getCurrentUser();
  const isPublic = 'is_public' in item ? item.is_public : false;
  const canDelete = !isPublic || (user && 'created_by' in item && item.created_by === user.id);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: 'pointer',
        backgroundColor: isSelected
          ? 'action.selected'
          : (isContainer && isOver)
            ? 'primary.light'
            : isPublic
              ? 'rgba(25, 118, 210, 0.05)'
              : 'inherit',
        position: 'relative',
        border: isContainer ? '2px dashed' : '1px solid',
        borderColor: isContainer ? (isOver ? 'primary.dark' : 'primary.main') : 'divider',
        transform: isContainer && isOver ? 'scale(1.02)' : 'scale(1)',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: 2 },
        mb: 1,
      }}
    >
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        <>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              {...attributes}
              {...listeners}
              sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}
            >
              <DragIcon fontSize="small" color="action" />
            </Box>

            <Box flex={1} display="flex" alignItems="center" gap={1} onClick={() => onSelect(item, isContainer)}>
              {isContainer ? (
                <FolderIcon fontSize="small" color="primary" />
              ) : (
                <FileIcon fontSize="small" color="action" />
              )}
              <Box>
                <Typography variant="subtitle2">
                  {'title' in item ? item.title : ('pattern_title' in item ? item.pattern_title : 'Sans titre')}
                </Typography>
                {isContainer && 'children_count' in item && (
                  <Typography variant="caption" color="textSecondary">
                    {item.children_count || 0} contenants, {item.exercises_count || 0} exercices
                  </Typography>
                )}
              </Box>
            </Box>

            <Box display="flex" gap={0.5} alignItems="center">
              {!isContainer && onTogglePublic && (
                <Switch
                  size="small"
                  checked={isPublic}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    onTogglePublic(item as Exercise, e.target.checked);
                  }}
                  sx={{ ml: 0.5 }}
                />
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRenameItem?.(item, isContainer);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>

              {canDelete && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isContainer) {
                      onDeleteContainer?.(item as ExerciseContainer);
                    } else {
                      onDeleteExercise?.(item as Exercise);
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
          {!isContainer && 'original_author_name' in item && item.original_author_name && (
            <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5, ml: 5 }}>
              {`Auteur original: ${item.original_author_name}`}
            </Typography>
          )}
        </>
      </CardContent>
    </Card>
  );
};

interface ExercisesProps {
  urlContainerId?: number | null;
  onNavChange?: (containerId: number | null, itemId: number | null, label?: string) => void;
}

const Exercises: React.FC<ExercisesProps> = ({ urlContainerId, onNavChange }) => {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [containers, setContainers] = useState<ExerciseContainer[]>([]);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [currentContainer, setCurrentContainer] = useState<ExerciseContainer | null>(null);
  const [navigationPath, setNavigationPath] = useState<ExerciseContainer[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setIsPublic] = useState(false);
  const [activeItemType, setActiveItemType] = useState<string>('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createType, setCreateType] = useState<'container' | 'exercise'>('exercise');
  const [containerTitle, setContainerTitle] = useState('');
  const [selectedPatternId, setSelectedPatternId] = useState<number | ''>('');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState<{ item: Exercise | ExerciseContainer, isContainer: boolean } | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [patternDialogOpen, setPatternDialogOpen] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState<ExercisePattern | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sync from URL (back/forward navigation)
  useEffect(() => {
    const currentId = currentContainer?.id ?? null;
    if (urlContainerId !== undefined && urlContainerId !== currentId) {
      if (urlContainerId === null) {
        setCurrentContainer(null);
        setNavigationPath([]);
      } else {
        const restoreContainer = async () => {
          try {
            const container = await exerciseContainerService.getContainer(urlContainerId);
            setCurrentContainer(container);
            const path: any[] = [];
            let cId: number | null = container.parent ?? null;
            while (cId) {
              const c = await exerciseContainerService.getContainer(cId);
              path.unshift(c);
              cId = c.parent ?? null;
            }
            path.push(container);
            setNavigationPath(path);
          } catch (err) {
            console.error('Error restoring container from URL:', err);
          }
        };
        restoreContainer();
      }
    }
  }, [urlContainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);

      if (currentContainer) {
        // Load contents of the current container
        const contents = await exerciseContainerService.getContainerContents(currentContainer.id);
        setContainers(Array.isArray(contents?.containers) ? contents.containers : []);
        setExercises(Array.isArray(contents?.exercises) ? contents.exercises : []);
      } else {
        // Load root level items
        const contents = await exerciseContainerService.getRootContents();
        setContainers(Array.isArray(contents?.containers) ? contents.containers : []);
        setExercises(Array.isArray(contents?.exercises) ? contents.exercises : []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast.error('Erreur lors du chargement des données');
      setContainers([]);
      setExercises([]);
    } finally {
      setLoading(false);
    }
  }, [currentContainer]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    setActiveItemType(id.startsWith('container-') ? 'container' : 'exercise');
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveItemType('');

    if (!over || !active) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Handle drop to parent folder
    if (overId === 'parent-folder') {
      await handleDropToParent(activeId);
      return;
    }

    // Handle drop into container
    const isActiveExercise = activeId.startsWith('exercise-');
    const isOverContainer = overId.startsWith('container-');

    if (isActiveExercise && isOverContainer) {
      const exerciseId = parseInt(activeId.replace('exercise-', ''));
      const containerId = parseInt(overId.replace('container-', ''));
      const exercise = exercises.find(e => e.id === exerciseId);

      if (exercise) {
        try {
          await exerciseService.updateExercise(exerciseId, {
            container: containerId
          });
          await loadItems();
          toast.success('Exercice déplacé dans le contenant');
        } catch (error) {
          toast.error('Erreur lors du déplacement');
        }
      }
      return;
    }

    // Handle regular reordering
    if (active.id !== over.id) {
      const allItems = [...containers, ...exercises];
      const activeIndex = allItems.findIndex(item =>
        `${('children_count' in item ? 'container' : 'exercise')}-${item.id}` === active.id
      );
      const overIndex = allItems.findIndex(item =>
        `${('children_count' in item ? 'container' : 'exercise')}-${item.id}` === over.id
      );

      if (activeIndex !== -1 && overIndex !== -1) {
        const newItems = arrayMove(allItems, activeIndex, overIndex);

        // Separate containers and exercises
        const newContainers = newItems.filter(item => 'children_count' in item) as ExerciseContainer[];
        const newExercises = newItems.filter(item => 'pattern_title' in item) as Exercise[];

        setContainers(newContainers);
        setExercises(newExercises);

        // Update order values
        const containerOrder = newContainers.map((item, index) => ({
          id: item.id,
          order: index,
          parent: currentContainer?.id || null
        }));
        const exerciseOrder = newExercises.map((item, index) => ({
          id: item.id,
          order: index + newContainers.length,
          container: currentContainer?.id || null
        }));

        // Save new order to backend
        if (containerOrder.length > 0) {
          exerciseContainerService.reorderContainers(containerOrder).catch(error => {
            console.error('Error reordering containers:', error);
            loadItems();
          });
        }
        if (exerciseOrder.length > 0) {
          exerciseService.reorderExercises(exerciseOrder).catch(error => {
            console.error('Error reordering exercises:', error);
            loadItems();
          });
        }
      }
    }
  };

  const handleItemSelect = (item: Exercise | ExerciseContainer, isContainer: boolean) => {
    if (isContainer) {
      const container = item as ExerciseContainer;
      setNavigationPath([...navigationPath, container]);
      setCurrentContainer(container);
      onNavChange?.(container.id, null, container.title);
      setSelectedExercise(null);
      setIsPublic(false);
    } else {
      const exercise = item as Exercise;
      setSelectedExercise(exercise);
      setIsPublic(exercise.is_public ?? false);
    }
  };

  const handleNavigateBack = () => {
    if (navigationPath.length > 0) {
      const newPath = [...navigationPath];
      newPath.pop();
      setNavigationPath(newPath);
      const newContainer = newPath[newPath.length - 1] || null;
      setCurrentContainer(newContainer);
      onNavChange?.(newContainer?.id ?? null, null, newContainer?.title);
    }
  };

  const handleNavigateTo = (index: number) => {
    const newPath = navigationPath.slice(0, index + 1);
    setNavigationPath(newPath);
    const newContainer = newPath[newPath.length - 1] || null;
    setCurrentContainer(newContainer);
    onNavChange?.(newContainer?.id ?? null, null, newContainer?.title);
  };

  const handleDropToParent = async (itemId: string) => {
    const parentId = navigationPath.length > 1
      ? navigationPath[navigationPath.length - 2].id
      : null;

    if (itemId.startsWith('exercise-')) {
      const exerciseId = parseInt(itemId.replace('exercise-', ''));
      const exercise = exercises.find(e => e.id === exerciseId);
      if (exercise) {
        try {
          await exerciseService.updateExercise(exerciseId, {
            container: parentId
          });
          await loadItems();
          toast.success('Exercice déplacé vers le niveau supérieur');
        } catch (error) {
          toast.error('Erreur lors du déplacement');
        }
      }
    } else if (itemId.startsWith('container-')) {
      const containerId = parseInt(itemId.replace('container-', ''));
      const container = containers.find(c => c.id === containerId);
      if (container) {
        try {
          await exerciseContainerService.updateContainer(containerId, {
            parent: parentId
          });
          await loadItems();
          toast.success('Contenant déplacé vers le niveau supérieur');
        } catch (error) {
          toast.error('Erreur lors du déplacement');
        }
      }
    }
  };

  const handleNew = () => {
    setCreateType('exercise');
    setContainerTitle('');
    setSelectedPatternId('');
    setSelectedPattern(null);
    setCreateDialogOpen(true);
  };

  const handlePatternSelect = (pattern: ExercisePattern) => {
    setSelectedPattern(pattern);
    setSelectedPatternId(pattern.id);
    setPatternDialogOpen(false);
  };

  const handleCreate = async () => {
    try {
      setLoading(true);

      if (createType === 'container') {
        if (!containerTitle.trim()) {
          toast.error('Veuillez entrer un titre pour le contenant');
          return;
        }

        await exerciseContainerService.createContainer({
          title: containerTitle,
          parent: currentContainer?.id || null
        });
        toast.success('Contenant créé avec succès');
      } else {
        if (!selectedPatternId) {
          toast.error('Veuillez sélectionner un pattern');
          return;
        }

        const newExercise = await exerciseService.createExerciseFromPattern(selectedPatternId as number);

        // Update with container if needed
        if (currentContainer) {
          await exerciseService.updateExercise(newExercise.id, {
            container: currentContainer.id
          });
        }

        toast.success('Exercice créé avec succès');
        setSelectedExercise(newExercise);
      }

      setCreateDialogOpen(false);
      await loadItems();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExercise = async (exercise: Exercise) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet exercice ?')) {
      try {
        await exerciseService.deleteExercise(exercise.id);
        toast.success('Exercice supprimé avec succès');
        if (selectedExercise?.id === exercise.id) {
          setSelectedExercise(null);
        }
        await loadItems();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleDeleteContainer = async (container: ExerciseContainer) => {
    const confirmDelete = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le contenant "${container.title}" ?\n` +
      `Son contenu sera déplacé vers le niveau parent.`
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);
      await exerciseContainerService.deleteContainer(container.id);
      toast.success(`Contenant "${container.title}" supprimé`);
      await loadItems();
    } catch (error) {
      toast.error('Erreur lors de la suppression du contenant');
    } finally {
      setLoading(false);
    }
  };

  const handleRenameItem = (item: Exercise | ExerciseContainer, isContainer: boolean) => {
    setItemToRename({ item, isContainer });
    setRenameTitle('title' in item ? item.title : ('pattern_title' in item ? item.pattern_title || '' : ''));
    setRenameDialogOpen(true);
  };

  const handleConfirmRename = async () => {
    if (!itemToRename || !renameTitle.trim()) return;

    try {
      setLoading(true);

      if (itemToRename.isContainer) {
        await exerciseContainerService.updateContainer(itemToRename.item.id, {
          title: renameTitle.trim()
        });
      } else {
        // Exercises don't have a title field, so we can't rename them
        toast.warning('Les exercices ne peuvent pas être renommés');
        setRenameDialogOpen(false);
        return;
      }

      toast.success('Renommage effectué avec succès');
      loadItems();
      setRenameDialogOpen(false);
      setItemToRename(null);
      setRenameTitle('');
    } catch (error: any) {
      toast.error('Erreur lors du renommage');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateExercise = async (exercise: Exercise) => {
    if (exercise.pattern) {
      try {
        const newExercise = await exerciseService.createExerciseFromPattern(exercise.pattern);

        // Update with container if needed
        if (currentContainer) {
          await exerciseService.updateExercise(newExercise.id, {
            container: currentContainer.id
          });
        }

        toast.success('Exercice régénéré avec succès');
        await loadItems();
        setSelectedExercise(newExercise);
      } catch (error) {
        toast.error('Erreur lors de la régénération');
      }
    }
  };

  const handleCopyLatex = () => {
    if (selectedExercise?.rendered_latex) {
      navigator.clipboard.writeText(selectedExercise.rendered_latex);
      toast.success('LaTeX copié dans le presse-papiers');
    }
  };

  const handleTogglePublic = async (exercise: Exercise, newIsPublic: boolean) => {
    try {
      await exerciseService.updateExercise(exercise.id, {
        is_public: newIsPublic,
      });
      toast.success(`Exercice rendu ${newIsPublic ? 'public' : 'privé'}`);

      // Update in the list
      setExercises(exercises.map(ex =>
        ex.id === exercise.id ? { ...ex, is_public: newIsPublic } : ex
      ));

      // Update selected exercise if it's the same
      if (selectedExercise?.id === exercise.id) {
        setSelectedExercise({ ...selectedExercise, is_public: newIsPublic });
        setIsPublic(newIsPublic);
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const allItems = [...containers, ...exercises];
  const sortableItems = allItems.map(item =>
    `${('children_count' in item ? 'container' : 'exercise')}-${item.id}`
  );

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Exercise List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            {/* Navigation Breadcrumbs */}
            {navigationPath.length > 0 && (
              <Box mb={2}>
                <Breadcrumbs>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setNavigationPath([]);
                      setCurrentContainer(null);
                      onNavChange?.(null, null, undefined);
                    }}
                    underline="hover"
                    color="inherit"
                  >
                    Exercices
                  </Link>
                  {navigationPath.map((container, index) => (
                    <Link
                      key={container.id}
                      component="button"
                      variant="body2"
                      onClick={() => handleNavigateTo(index)}
                      underline="hover"
                      color={index === navigationPath.length - 1 ? "text.primary" : "inherit"}
                    >
                      {container.title}
                    </Link>
                  ))}
                </Breadcrumbs>
              </Box>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                {navigationPath.length > 0 && (
                  <IconButton size="small" onClick={handleNavigateBack}>
                    <ArrowBackIcon />
                  </IconButton>
                )}
                <Typography variant="h6">
                  {currentContainer ? currentContainer.title : 'Exercices'}
                </Typography>
              </Box>
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={handleNew}
                disabled={loading}
              >
                Nouveau
              </Button>
            </Box>

            {loading ? (
              <CircularProgress />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortableItems}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={1}>
                    {currentContainer && (
                      <ParentDropZone
                        parentContainer={navigationPath[navigationPath.length - 2] || null}
                        onDrop={handleDropToParent}
                        onClick={handleNavigateBack}
                      />
                    )}
                    {allItems.map((item) => {
                      const isContainer = 'children_count' in item;
                      return (
                        <SortableItem
                          key={`${isContainer ? 'container' : 'exercise'}-${item.id}`}
                          item={item}
                          isContainer={isContainer}
                          isSelected={!isContainer && selectedExercise?.id === item.id}
                          onSelect={handleItemSelect}
                          onDeleteContainer={handleDeleteContainer}
                          onDeleteExercise={handleDeleteExercise}
                          onRenameItem={handleRenameItem}
                          onTogglePublic={handleTogglePublic}
                          activeItemType={activeItemType}
                        />
                      );
                    })}
                  </Stack>
                </SortableContext>
              </DndContext>
            )}
          </Paper>
        </Grid>

        {/* Right Panel - Exercise Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{
            p: 3,
            backgroundColor: selectedExercise?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
            borderRadius: 2
          }}>
            {selectedExercise ? (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">
                    Exercice - {selectedExercise.pattern_title}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Copier le LaTeX">
                      <IconButton onClick={handleCopyLatex}>
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Régénérer avec de nouvelles valeurs">
                      <IconButton
                        onClick={() => handleRegenerateExercise(selectedExercise)}
                        disabled={!selectedExercise.pattern}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Generated Values */}
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Valeurs générées
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <pre style={{
                      fontSize: '0.85em',
                      margin: 0,
                      fontFamily: 'monospace',
                    }}>
                      {JSON.stringify(selectedExercise.generated_values, null, 2)}
                    </pre>
                  </Paper>
                </Box>

                {/* LaTeX Code */}
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Code LaTeX
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <pre style={{
                      fontSize: '0.85em',
                      margin: 0,
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                      {selectedExercise.rendered_latex}
                    </pre>
                  </Paper>
                </Box>

                {/* PDF Preview */}
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Aperçu PDF
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <PdfViewer
                      url={`/exercises/${selectedExercise.id}/compile_pdf/`}
                      title={`exercise_${selectedExercise.id}`}
                    />
                  </Paper>
                </Box>

                {/* Tags */}
                {selectedExercise.tags && selectedExercise.tags.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Tags
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {selectedExercise.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="400px"
                color="text.secondary"
              >
                <Typography variant="h6" gutterBottom>
                  Aucun exercice sélectionné
                </Typography>
                <Typography variant="body2">
                  Sélectionnez un exercice dans la liste ou créez-en un nouveau
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ToggleButtonGroup
              value={createType}
              exclusive
              onChange={(e, newType) => newType && setCreateType(newType)}
              fullWidth
            >
              <ToggleButton value="container">
                <FolderIcon sx={{ mr: 1 }} />
                Contenant
              </ToggleButton>
              <ToggleButton value="exercise">
                <FileIcon sx={{ mr: 1 }} />
                Exercice
              </ToggleButton>
            </ToggleButtonGroup>

            {createType === 'container' ? (
              <TextField
                autoFocus
                label="Titre du contenant"
                fullWidth
                value={containerTitle}
                onChange={(e) => setContainerTitle(e.target.value)}
              />
            ) : (
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Sélectionnez un pattern d'exercice pour générer un nouvel exercice
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setPatternDialogOpen(true)}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.5 }}
                >
                  {selectedPattern ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <FileIcon fontSize="small" />
                      <Typography>
                        {selectedPattern.title} ({selectedPattern.exercise_count || 0} exercices)
                      </Typography>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      Sélectionner un pattern...
                    </Typography>
                  )}
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleCreate} variant="contained">
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Renommer</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              label="Nouveau titre"
              fullWidth
              value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
            />
            {itemToRename && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  {`Créé le: ${new Date(itemToRename.item.created_at || '').toLocaleDateString('fr-FR')}`}
                </Typography>
                <br />
                {'updated_at' in itemToRename.item && (
                  <Typography variant="caption" color="textSecondary">
                    {`Modifié le: ${new Date(itemToRename.item.updated_at || '').toLocaleDateString('fr-FR')}`}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleConfirmRename} variant="contained">
            Renommer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pattern Selection Dialog */}
      <PatternSelectionDialog
        open={patternDialogOpen}
        onClose={() => setPatternDialogOpen(false)}
        onSelect={handlePatternSelect}
      />
    </Box>
  );
};

export default Exercises;